import React, { useState, useMemo } from "react";
import { Modal, Tooltip } from "antd";
import {
  CheckCircle,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  CreditCard,
  Search,
  X,
  Copy,
  ArrowRight,
} from "lucide-react";
import SubscribeModulePicker, {
  ProviderBadge,
  getProviderTheme,
} from "../SubscribeModulePicker";
import {
  computeSubscribeMonthlyTotal,
  formatPlanModuleSummary,
  getDefaultOrgPlan,
  getOrgKey,
  getOrgPlanStatus,
} from "../../utils/subscribeModules";
import { NEW_CUSTOMER_BILLING_COMPACT_LINE } from "../../constants/newCustomerBilling";
import "./multi-subscribe-wizard-modal.scss";
import CouponInput from "../CouponInput";

function OrgSidebarItem({
  org,
  plan,
  isActive,
  onSelect,
  onToggleInclude,
}) {
  const provider = org.provider ?? "xero";
  const theme = getProviderTheme(provider);
  const status = getOrgPlanStatus(plan);
  const isIncluded = status === "included";
  const monthlyTotal =
    plan.skipped || !plan.accounts
      ? null
      : computeSubscribeMonthlyTotal(plan);
  const orgKey = getOrgKey(org);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect?.(orgKey)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect?.(orgKey);
        }
      }}
      className={`multi-subscribe-wizard__sidebar-item group relative w-full rounded-lg border px-2.5 py-2 text-left transition-all cursor-pointer ${
        isActive
          ? "border-sky-300 bg-sky-50/60 shadow-xs"
          : "border-transparent bg-white/70 hover:bg-gray-100/80 hover:border-gray-200"
      }`}
      style={
        isActive
          ? { borderLeft: `3px solid ${theme.primary}`, backgroundColor: "#f0f9ff" }
          : undefined
      }
    >
      <div className="flex items-center gap-2">
        {/* Quick Checkbox to include/skip without changing page */}
        <input
          type="checkbox"
          checked={isIncluded}
          onChange={(e) => {
            e.stopPropagation();
            onToggleInclude?.(orgKey, e.target.checked);
          }}
          onClick={(e) => e.stopPropagation()}
          title={isIncluded ? "Click to skip this org" : "Click to include this org"}
          className="h-4 w-4 shrink-0 rounded border-gray-300 text-sky-600 focus:ring-sky-500 cursor-pointer"
        />

        <div className="min-w-0 flex-1">
          <p
            className={`truncate text-xs font-medium ${
              isActive
                ? "text-sky-950 font-semibold"
                : isIncluded
                ? "text-gray-900"
                : "text-gray-500 line-through decoration-gray-300"
            }`}
            title={org.account}
          >
            {org.account}
          </p>
          <div className="mt-0.5 flex items-center gap-1.5">
            <span
              className={`inline-block text-[10px] font-semibold px-1.5 py-0.2 rounded ${
                provider === "qbo"
                  ? "bg-green-100 text-green-800"
                  : "bg-sky-100 text-sky-800"
              }`}
            >
              {provider === "qbo" ? "QBO" : "XERO"}
            </span>
            {plan.coupon_code && isIncluded && (
              <span className="text-[10px] text-emerald-600 font-semibold">
                Promo
              </span>
            )}
          </div>
        </div>

        <div className="shrink-0 text-right">
          {isIncluded ? (
            <span className="text-xs font-semibold text-emerald-600">
              ${monthlyTotal.toFixed(2)}
            </span>
          ) : (
            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-400">
              Skipped
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryStep({
  orgs,
  orgPlans,
  paymentCards = [],
  defaultPaymentMethodId = null,
  selectedPaymentMethodId = null,
  onPaymentMethodChange,
  onAddNewCard,
  onApplyCouponToAll,
  onRemoveCouponFromAll,
  onExcludeOrg,
  onReincludeOrg,
}) {
  const [batchCouponCode, setBatchCouponCode] = React.useState("");
  const [batchCouponDiscount, setBatchCouponDiscount] = React.useState(null);
  const [redemptionNotice, setRedemptionNotice] = React.useState("");
  const [summarySearch, setSummarySearch] = React.useState("");
  const [summaryProviderFilter, setSummaryProviderFilter] = React.useState("all");
  const [showSkippedAccordion, setShowSkippedAccordion] = React.useState(false);

  const included = useMemo(
    () =>
      orgs.filter((org) => {
        const plan = orgPlans[getOrgKey(org)] ?? orgPlans[org.id];
        return plan && !plan.skipped && plan.accounts;
      }),
    [orgs, orgPlans]
  );

  const skipped = useMemo(
    () =>
      orgs.filter((org) => {
        const plan = orgPlans[getOrgKey(org)] ?? orgPlans[org.id];
        return !plan || plan.skipped || !plan.accounts;
      }),
    [orgs, orgPlans]
  );

  const grandTotal = useMemo(
    () =>
      included.reduce(
        (sum, org) =>
          sum +
          computeSubscribeMonthlyTotal(
            orgPlans[getOrgKey(org)] ?? orgPlans[org.id]
          ),
        0
      ),
    [included, orgPlans]
  );

  const xeroIncludedCount = useMemo(
    () => included.filter((o) => (o.provider ?? "xero") === "xero").length,
    [included]
  );
  const qboIncludedCount = useMemo(
    () => included.filter((o) => o.provider === "qbo").length,
    [included]
  );

  const filteredIncluded = useMemo(() => {
    return included.filter((org) => {
      const provider = org.provider ?? "xero";
      if (summaryProviderFilter !== "all" && provider !== summaryProviderFilter) {
        return false;
      }
      if (summarySearch.trim()) {
        const q = summarySearch.trim().toLowerCase();
        return (org.account || "").toLowerCase().includes(q);
      }
      return true;
    });
  }, [included, summaryProviderFilter, summarySearch]);

  const chargeImmediatelyCount = included.filter(
    (org) => org.subscribe_will_charge_immediately
  ).length;

  const currentPaymentMethodId =
    selectedPaymentMethodId ||
    (defaultPaymentMethodId &&
    paymentCards.some((c) => c.id === defaultPaymentMethodId)
      ? defaultPaymentMethodId
      : paymentCards.length > 0
      ? paymentCards[0].id
      : null);

  const handleDiscountApplied = (discountInfo) => {
    if (!discountInfo) {
      setBatchCouponDiscount(null);
      setRedemptionNotice("");
      onRemoveCouponFromAll?.();
      return;
    }

    const availableRedemptions =
      discountInfo.max_redemptions != null
        ? Math.max(
            0,
            discountInfo.max_redemptions - (discountInfo.times_redeemed || 0)
          )
        : Infinity;

    if (availableRedemptions >= included.length) {
      setBatchCouponDiscount(discountInfo);
      setRedemptionNotice("");
      onApplyCouponToAll?.(batchCouponCode || discountInfo.code, discountInfo);
    } else {
      const code = batchCouponCode || discountInfo.code || "entered";
      setBatchCouponDiscount(null);
      onRemoveCouponFromAll?.();
      setRedemptionNotice(
        `✖ Promo code ${code} cannot be applied in bulk (Only ${availableRedemptions} redemption${availableRedemptions === 1 ? "" : "s"} available for ${included.length} organizations).`
      );
    }
  };

  return (
    <div className="space-y-3.5">
      <div>
        <h3 className="text-base sm:text-lg font-bold text-gray-900">
          Review & Confirm Subscriptions
        </h3>
        <p className="mt-0.5 text-xs text-gray-600">
          Review your selected organizations and modules before confirming.
        </p>
      </div>

      {/* Summary Stat Cards - Compact Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div className="rounded-xl border border-sky-100 bg-sky-50/70 p-2.5">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-sky-700">
            To Subscribe
          </p>
          <p className="mt-0.5 text-lg font-bold text-sky-950">{included.length}</p>
          <p className="text-[10px] text-sky-600 truncate">
            {xeroIncludedCount} Xero · {qboIncludedCount} QBO
          </p>
        </div>

        <div className="rounded-xl border border-emerald-100 bg-emerald-50/70 p-2.5">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700">
            Monthly Total
          </p>
          <p className="mt-0.5 text-lg font-bold text-emerald-800">
            ${grandTotal.toFixed(2)}
          </p>
          <p className="text-[10px] text-emerald-600">USD billed monthly</p>
        </div>

        <div className="rounded-xl border border-gray-200 bg-gray-50/80 p-2.5">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
            Skipped Orgs
          </p>
          <p className="mt-0.5 text-lg font-bold text-gray-700">{skipped.length}</p>
          <p className="text-[10px] text-gray-500">Will not be charged</p>
        </div>

        <div className="rounded-xl border border-indigo-100 bg-indigo-50/70 p-2.5">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-indigo-700">
            Trial Status
          </p>
          <p className="mt-0.5 text-sm font-bold text-indigo-950">
            {chargeImmediatelyCount === 0
              ? "30-Day Free Trial"
              : `${chargeImmediatelyCount} Immediate`}
          </p>
          <p className="text-[10px] text-indigo-600 truncate">
            {chargeImmediatelyCount === 0
              ? "Autopay after trial"
              : `${included.length - chargeImmediatelyCount} with trial`}
          </p>
        </div>
      </div>

      {/* Payment Method Selector */}
      <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-2xs">
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <label className="text-xs font-semibold uppercase tracking-wider text-gray-700 flex items-center gap-1.5">
            <CreditCard className="h-4 w-4 text-gray-500" />
            Payment Method
          </label>
          {onAddNewCard && (
            <button
              type="button"
              onClick={onAddNewCard}
              className="text-xs font-semibold text-sky-600 hover:text-sky-800 hover:underline cursor-pointer"
            >
              + Add New Card
            </button>
          )}
        </div>

        {paymentCards.length > 0 ? (
          <select
            value={currentPaymentMethodId || ""}
            onChange={(e) => {
              if (e.target.value === "ADD_NEW") {
                onAddNewCard?.();
              } else {
                onPaymentMethodChange?.(e.target.value);
              }
            }}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs sm:text-sm text-gray-900 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
          >
            {paymentCards.map((cardItem) => {
              const cardObj = cardItem.card || cardItem;
              const isDefault = cardItem.id === defaultPaymentMethodId;
              const brand = (cardObj.brand || cardObj.display_brand || "Card").toUpperCase();
              const last4 = cardObj.last4 || "";
              const expMonth = cardObj.exp_month
                ? String(cardObj.exp_month).padStart(2, "0")
                : "";
              const expYear = cardObj.exp_year || "";
              const expiryText =
                expMonth && expYear ? ` (Expires ${expMonth}/${expYear})` : "";
              return (
                <option key={cardItem.id} value={cardItem.id}>
                  {brand} •••• {last4}{expiryText}{isDefault ? " — (Default)" : ""}
                </option>
              );
            })}
          </select>
        ) : (
          <div className="flex items-center justify-between rounded-lg border border-dashed border-gray-300 p-2.5 bg-gray-50 text-xs sm:text-sm">
            <span className="text-gray-500 text-xs">No payment card on file</span>
            <button
              type="button"
              onClick={onAddNewCard}
              className="rounded bg-sky-600 px-3 py-1 text-xs font-semibold text-white hover:bg-sky-700 transition"
            >
              Add Card
            </button>
          </div>
        )}
      </div>

      {/* Bulk Promo Code */}
      {included.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-3">
          <CouponInput
            label={
              included.length > 1
                ? `Have a Promo Code? (Apply to All ${included.length} Organizations)`
                : "Have a Promo Code?"
            }
            helperText={
              included.length > 1
                ? "Enter a promo code here to apply this discount across all selected organizations at once."
                : null
            }
            value={batchCouponCode}
            onChange={(code) => {
              setBatchCouponCode(code);
              if (!code) {
                setBatchCouponDiscount(null);
                setRedemptionNotice("");
              }
            }}
            appliedDiscount={batchCouponDiscount}
            onDiscountApplied={handleDiscountApplied}
          />

          {batchCouponDiscount ? (
            <div className="mt-2 text-xs space-y-1">
              <div className="flex flex-wrap items-center gap-1.5 text-gray-700">
                <span className="font-semibold text-gray-800">Coupon Duration:</span>
                <span className="rounded bg-emerald-100 px-2 py-0.5 font-medium text-emerald-800">
                  {batchCouponDiscount.duration === "once"
                    ? "First month only (1-time duration)"
                    : batchCouponDiscount.duration === "repeating"
                    ? `${batchCouponDiscount.duration_in_months || ""} Months recurring`
                    : "Forever (all recurring invoices)"}
                </span>
              </div>
              <p className="text-emerald-700 font-medium">
                ✓ {included.length === 1
                  ? "Applied to the selected organization."
                  : `Automatically applied to all ${included.length} selected organizations.`}
              </p>
            </div>
          ) : redemptionNotice ? (
            <div className="mt-2">
              <p className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs font-medium text-red-800">
                {redemptionNotice}
              </p>
            </div>
          ) : null}
        </div>
      )}

      {/* Included Organizations Table with Dedicated Scrollbar Slider */}
      {included.length > 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden shadow-2xs">
          {/* Table Toolbar with Search & Filter */}
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 bg-gray-50/90 px-3 py-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-800">
                Subscribing Organizations ({filteredIncluded.length}
                {filteredIncluded.length !== included.length ? ` of ${included.length}` : ""})
              </span>
              <span className="text-[10px] text-gray-400 hidden sm:inline">
                • Scroll table to view all
              </span>
            </div>

            <div className="flex items-center gap-2">
              {/* Provider filter */}
              {xeroIncludedCount > 0 && qboIncludedCount > 0 && (
                <div className="flex rounded-md border border-gray-300 bg-white p-0.5 text-[11px]">
                  <button
                    type="button"
                    onClick={() => setSummaryProviderFilter("all")}
                    className={`rounded px-2 py-0.5 font-medium transition cursor-pointer ${
                      summaryProviderFilter === "all"
                        ? "bg-sky-600 text-white"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    All ({included.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setSummaryProviderFilter("xero")}
                    className={`rounded px-2 py-0.5 font-medium transition cursor-pointer ${
                      summaryProviderFilter === "xero"
                        ? "bg-sky-600 text-white"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    Xero ({xeroIncludedCount})
                  </button>
                  <button
                    type="button"
                    onClick={() => setSummaryProviderFilter("qbo")}
                    className={`rounded px-2 py-0.5 font-medium transition cursor-pointer ${
                      summaryProviderFilter === "qbo"
                        ? "bg-green-600 text-white"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    QBO ({qboIncludedCount})
                  </button>
                </div>
              )}

              {/* Table search input */}
              <div className="relative">
                <Search className="pointer-events-none absolute left-2 top-2 h-3.5 w-3.5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search organizations..."
                  value={summarySearch}
                  onChange={(e) => setSummarySearch(e.target.value)}
                  className="w-36 sm:w-44 rounded-md border border-gray-300 bg-white pl-7 pr-6 py-1 text-xs text-gray-900 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
                />
                {summarySearch && (
                  <button
                    type="button"
                    onClick={() => setSummarySearch("")}
                    className="absolute right-1.5 top-1.5 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Table Container with visible scrollbar slider */}
          <div className="max-h-64 sm:max-h-72 overflow-y-scroll multi-subscribe-wizard__table-scroll">
            <table className="w-full text-xs sm:text-sm">
              <thead className="sticky top-0 bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-200 z-10">
                <tr>
                  <th className="px-3 sm:px-4 py-2 font-medium">Organization</th>
                  <th className="px-3 sm:px-4 py-2 font-medium">Modules & Retention</th>
                  <th className="px-3 sm:px-4 py-2 font-medium text-right">Monthly</th>
                  <th className="px-2 sm:px-3 py-2 text-center w-16">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredIncluded.map((org) => {
                  const key = getOrgKey(org);
                  const plan = orgPlans[key] ?? orgPlans[org.id];
                  const provider = org.provider ?? "xero";
                  const baseTotal = computeSubscribeMonthlyTotal({
                    ...plan,
                    coupon_discount: null,
                  });
                  const finalTotal = computeSubscribeMonthlyTotal(plan);

                  return (
                    <tr key={key} className="hover:bg-gray-50/70 transition-colors">
                      <td className="px-3 sm:px-4 py-2 font-medium text-gray-900">
                        <div className="flex items-center gap-1.5">
                          <span
                            className={`inline-block text-[10px] font-semibold px-1.5 py-0.2 rounded shrink-0 ${
                              provider === "qbo"
                                ? "bg-green-100 text-green-800"
                                : "bg-sky-100 text-sky-800"
                            }`}
                          >
                            {provider === "qbo" ? "QBO" : "XERO"}
                          </span>
                          <span className="truncate">{org.account}</span>
                        </div>
                        {plan.coupon_code && (
                          <div className="mt-0.5 text-[11px] text-emerald-600 font-medium pl-8">
                            Promo: {plan.coupon_code}
                            {plan.coupon_discount?.percent_off != null &&
                              ` (${plan.coupon_discount.percent_off}% off)`}
                            {plan.coupon_discount?.amount_off != null &&
                              ` ($${Number(plan.coupon_discount.amount_off).toFixed(2)} off)`}
                          </div>
                        )}
                      </td>
                      <td className="px-3 sm:px-4 py-2 text-gray-600 text-xs">
                        {formatPlanModuleSummary(plan, provider, org.country_code)}
                      </td>
                      <td className="px-3 sm:px-4 py-2 text-right font-medium">
                        {plan.coupon_discount ? (
                          <div>
                            <span className="text-[11px] text-gray-400 line-through mr-1.5">
                              ${baseTotal.toFixed(2)}
                            </span>
                            <span className="font-bold text-emerald-600">
                              ${finalTotal.toFixed(2)}
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-900 font-semibold">
                            ${finalTotal.toFixed(2)}
                          </span>
                        )}
                      </td>
                      <td className="px-2 sm:px-3 py-2 text-center">
                        <Tooltip title="Exclude from this batch">
                          <button
                            type="button"
                            onClick={() => onExcludeOrg?.(key)}
                            className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 transition cursor-pointer"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </Tooltip>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="border-t border-gray-200 bg-gray-50 px-4 py-2 flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-700">
              Total monthly cost ({included.length} organizations)
            </span>
            <span className="text-base sm:text-lg font-bold text-gray-900">
              ${grandTotal.toFixed(2)}
              <span className="text-xs font-normal text-gray-500"> /mo</span>
            </span>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-xs sm:text-sm text-amber-900">
          <p className="font-semibold">No organizations selected for subscription.</p>
          <p className="mt-1 text-amber-800">
            Click on organizations on the left sidebar to include at least one organization.
          </p>
        </div>
      )}

      {/* Skipped Organizations Accordion */}
      {skipped.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-3">
          <button
            type="button"
            onClick={() => setShowSkippedAccordion(!showSkippedAccordion)}
            className="flex w-full items-center justify-between text-left text-xs font-medium text-gray-700 hover:text-gray-900 cursor-pointer"
          >
            <span className="flex items-center gap-1.5">
              <span className="font-semibold uppercase tracking-wider text-gray-500">
                Skipped Organizations ({skipped.length})
              </span>
              <span className="text-[11px] text-gray-400">— will not be subscribed</span>
            </span>
            {showSkippedAccordion ? (
              <ChevronUp className="h-4 w-4 text-gray-500" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-500" />
            )}
          </button>

          {showSkippedAccordion && (
            <div className="mt-2.5 pt-2 border-t border-gray-200">
              <p className="text-[11px] text-gray-500 mb-2">
                Click "+ Add" on any organization to include it in this subscription batch:
              </p>
              <div className="flex flex-wrap gap-1.5 max-h-36 overflow-y-auto p-1 multi-subscribe-wizard__sidebar-scroll">
                {skipped.map((org) => {
                  const key = getOrgKey(org);
                  const provider = org.provider ?? "xero";
                  return (
                    <span
                      key={key}
                      className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700"
                    >
                      <span
                        className={`text-[9px] font-bold px-1 rounded ${
                          provider === "qbo"
                            ? "bg-green-100 text-green-800"
                            : "bg-sky-100 text-sky-800"
                        }`}
                      >
                        {provider === "qbo" ? "QBO" : "XERO"}
                      </span>
                      <span className="truncate max-w-[140px]">{org.account}</span>
                      <button
                        type="button"
                        onClick={() => onReincludeOrg?.(key)}
                        className="text-[10px] font-semibold text-sky-600 hover:text-sky-800 hover:underline cursor-pointer ml-1"
                        title="Include in subscription"
                      >
                        + Add
                      </button>
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Trial / Billing Explanation */}
      {included.length > 0 && (
        <div className="rounded-lg border border-sky-200 bg-sky-50 px-3.5 py-2.5 text-xs sm:text-sm text-sky-950">
          {chargeImmediatelyCount === 0 ? (
            <p>{NEW_CUSTOMER_BILLING_COMPACT_LINE}</p>
          ) : chargeImmediatelyCount === included.length ? (
            <p>
              {included.length === 1
                ? "This organization has already used its free trial and will be charged immediately on your chosen payment method, then monthly on autopay."
                : "All selected organizations have already used their free trial and will be charged immediately on your chosen payment method, then monthly on autopay."}
            </p>
          ) : (
            <p>
              {chargeImmediatelyCount} of {included.length} organizations will be
              charged immediately (prior trial used). The remaining{" "}
              {included.length - chargeImmediatelyCount} will start with a 30-day free
              trial before auto-billing on your chosen payment method.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default function MultiSubscribeWizardModal({
  open,
  onClose,
  orgs = [],
  orgPlans = {},
  currentStepIndex = 0,
  showSummary = false,
  paymentCards = [],
  defaultPaymentMethodId = null,
  selectedPaymentMethodId = null,
  onPaymentMethodChange,
  onAddNewCard,
  onOrgSelect,
  onPlanChange,
  onSkipOrg,
  onSkipAll,
  onIncludeAll,
  onApplySettingsToAll,
  onApplyCouponToAll,
  onRemoveCouponFromAll,
  onNext,
  onBack,
  onConfirm,
  confirming = false,
}) {
  const isMulti = orgs.length > 1;

  // Search & Filter state for 50+ organizations
  const [searchTerm, setSearchTerm] = useState("");
  const [providerFilter, setProviderFilter] = useState("all"); // 'all' | 'xero' | 'qbo'
  const [statusFilter, setStatusFilter] = useState("all"); // 'all' | 'included' | 'skipped'

  const currentOrg = showSummary ? null : orgs[currentStepIndex];
  const currentOrgKey = currentOrg ? getOrgKey(currentOrg) : "";
  const provider = currentOrg?.provider ?? "xero";
  const theme = getProviderTheme(provider);
  const isQbo = provider === "qbo";
  const currentPlan = currentOrg
    ? orgPlans[currentOrgKey] ?? orgPlans[currentOrg.id] ?? getDefaultOrgPlan()
    : getDefaultOrgPlan();

  const isLastOrg = currentStepIndex >= orgs.length - 1;

  const includedOrgs = useMemo(
    () =>
      orgs.filter((org) => {
        const plan = orgPlans[getOrgKey(org)] ?? orgPlans[org.id];
        return plan && !plan.skipped && plan.accounts;
      }),
    [orgs, orgPlans]
  );
  const includedCount = includedOrgs.length;
  const skippedCount = orgs.length - includedCount;

  const grandTotal = useMemo(
    () =>
      includedOrgs.reduce(
        (sum, org) =>
          sum +
          computeSubscribeMonthlyTotal(
            orgPlans[getOrgKey(org)] ?? orgPlans[org.id]
          ),
        0
      ),
    [includedOrgs, orgPlans]
  );

  const currentPaymentMethodId =
    selectedPaymentMethodId ||
    (defaultPaymentMethodId &&
    paymentCards.some((c) => c.id === defaultPaymentMethodId)
      ? defaultPaymentMethodId
      : paymentCards.length > 0
      ? paymentCards[0].id
      : null);

  const xeroOrgs = useMemo(
    () => orgs.filter((o) => (o.provider ?? "xero") === "xero"),
    [orgs]
  );
  const qboOrgs = useMemo(
    () => orgs.filter((o) => o.provider === "qbo"),
    [orgs]
  );

  // Filtered organizations in sidebar
  const filteredOrgs = useMemo(() => {
    return orgs.filter((org) => {
      const p = org.provider ?? "xero";
      if (providerFilter !== "all" && p !== providerFilter) {
        return false;
      }
      const plan = orgPlans[getOrgKey(org)] ?? orgPlans[org.id];
      const isInc = plan && !plan.skipped && plan.accounts;
      if (statusFilter === "included" && !isInc) return false;
      if (statusFilter === "skipped" && isInc) return false;

      if (searchTerm.trim()) {
        const query = searchTerm.trim().toLowerCase();
        return (org.account || "").toLowerCase().includes(query);
      }
      return true;
    });
  }, [orgs, orgPlans, providerFilter, statusFilter, searchTerm]);

  // Quick toggle include/skip directly from sidebar checkbox or review table
  const handleToggleInclude = (orgKey, shouldInclude) => {
    if (shouldInclude) {
      onPlanChange?.(orgKey, {
        accounts: true,
        skipped: false,
      });
    } else {
      onPlanChange?.(orgKey, {
        accounts: false,
        files: false,
        history_and_notes: false,
        payroll: false,
        skipped: true,
      });
    }
  };

  const handleSubPlanChange = (e) => {
    if (!currentOrg) return;
    if (e?.patch) {
      onPlanChange(currentOrgKey, e.patch);
      return;
    }
    const { name, checked, value } = e.target;
    if (name === "retention_days") {
      onPlanChange(currentOrgKey, { retention_days: Number(value) });
      return;
    }
    if (name === "accounts" && !checked) {
      onPlanChange(currentOrgKey, {
        accounts: false,
        files: false,
        history_and_notes: false,
        payroll: false,
        retention_days: 7,
        skipped: true,
      });
    } else if (name === "accounts" && checked) {
      onPlanChange(currentOrgKey, {
        accounts: true,
        skipped: false,
      });
    } else {
      onPlanChange(currentOrgKey, { [name]: checked });
    }
  };

  const modalWidth = isMulti ? 990 : isQbo ? 560 : 640;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={modalWidth}
      centered
      destroyOnClose
      className="multi-subscribe-wizard-modal"
      styles={{ body: { padding: 0 } }}
    >
      <div className="flex flex-col p-4 sm:p-5 h-[88vh] max-h-[780px] min-h-[560px]">
        {/* Header with Title and Informative Stats Badge (NO duplicate button) */}
        <div className="mb-3 flex items-center justify-between border-b border-gray-100 pb-2.5 shrink-0">
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2">
              <span>Subscribe Organizations</span>
              {isMulti && (
                <span className="rounded-full bg-sky-100 px-2.5 py-0.5 text-xs font-semibold text-sky-800">
                  {includedCount} of {orgs.length} selected (${grandTotal.toFixed(2)}/mo)
                </span>
              )}
            </h2>
            <p className="text-xs text-gray-500">
              {isMulti
                ? "Configure backup modules, search & select organizations, and review all at once."
                : "Configure modules for your organization backup subscription."}
            </p>
          </div>
        </div>

        {isMulti ? (
          <div className="flex flex-col md:flex-row gap-4 md:gap-5 flex-1 min-h-0 overflow-hidden">
            {/* Left Sidebar: Search, Filters, Bulk Actions & Org List */}
            <aside className="w-full md:w-72 lg:w-80 shrink-0 flex flex-col h-full min-h-0 border-b md:border-b-0 md:border-r border-gray-200/80 pb-3 md:pb-0 pr-0 md:pr-4">
              {/* Search Bar */}
              <div className="relative mb-2 shrink-0">
                <Search className="pointer-events-none absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by organization name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-gray-50/70 pl-8 pr-7 py-1.5 text-xs text-gray-900 placeholder:text-gray-400 focus:border-sky-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-sky-500"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm("")}
                    className="absolute right-2 top-2 text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              {/* Filter Pills */}
              <div className="mb-2 flex flex-wrap gap-1 shrink-0">
                <button
                  type="button"
                  onClick={() => setProviderFilter("all")}
                  className={`rounded-md px-2 py-0.5 text-[11px] font-medium transition cursor-pointer ${
                    providerFilter === "all"
                      ? "bg-gray-800 text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  All ({orgs.length})
                </button>
                {xeroOrgs.length > 0 && (
                  <button
                    type="button"
                    onClick={() =>
                      setProviderFilter(providerFilter === "xero" ? "all" : "xero")
                    }
                    className={`rounded-md px-2 py-0.5 text-[11px] font-medium transition cursor-pointer ${
                      providerFilter === "xero"
                        ? "bg-sky-600 text-white"
                        : "bg-sky-50 text-sky-700 hover:bg-sky-100"
                    }`}
                  >
                    Xero ({xeroOrgs.length})
                  </button>
                )}
                {qboOrgs.length > 0 && (
                  <button
                    type="button"
                    onClick={() =>
                      setProviderFilter(providerFilter === "qbo" ? "all" : "qbo")
                    }
                    className={`rounded-md px-2 py-0.5 text-[11px] font-medium transition cursor-pointer ${
                      providerFilter === "qbo"
                        ? "bg-green-600 text-white"
                        : "bg-green-50 text-green-700 hover:bg-green-100"
                    }`}
                  >
                    QBO ({qboOrgs.length})
                  </button>
                )}
                <button
                  type="button"
                  onClick={() =>
                    setStatusFilter(statusFilter === "included" ? "all" : "included")
                  }
                  className={`rounded-md px-2 py-0.5 text-[11px] font-medium transition cursor-pointer ${
                    statusFilter === "included"
                      ? "bg-emerald-600 text-white"
                      : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                  }`}
                >
                  Subscribed ({includedCount})
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setStatusFilter(statusFilter === "skipped" ? "all" : "skipped")
                  }
                  className={`rounded-md px-2 py-0.5 text-[11px] font-medium transition cursor-pointer ${
                    statusFilter === "skipped"
                      ? "bg-gray-600 text-white"
                      : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                  }`}
                >
                  Skipped ({skippedCount})
                </button>
              </div>

              {/* Bulk Quick Actions Toolbar */}
              <div className="mb-2 flex items-center justify-between border-y border-gray-100 py-1.5 px-0.5 text-[11px] shrink-0">
                <span className="font-semibold uppercase tracking-wider text-gray-400">
                  {filteredOrgs.length} {filteredOrgs.length === 1 ? "Org" : "Orgs"}
                  {filteredOrgs.length !== orgs.length ? ` (of ${orgs.length})` : ""}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      if (filteredOrgs.length === orgs.length) {
                        onIncludeAll?.();
                      } else {
                        filteredOrgs.forEach((o) =>
                          handleToggleInclude(getOrgKey(o), true)
                        );
                      }
                    }}
                    className="font-semibold text-sky-600 hover:text-sky-800 hover:underline cursor-pointer"
                  >
                    Include All
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    type="button"
                    onClick={() => {
                      if (filteredOrgs.length === orgs.length) {
                        onSkipAll?.();
                      } else {
                        filteredOrgs.forEach((o) =>
                          handleToggleInclude(getOrgKey(o), false)
                        );
                      }
                    }}
                    className="font-semibold text-gray-500 hover:text-gray-800 hover:underline cursor-pointer"
                  >
                    Skip All
                  </button>
                </div>
              </div>

              {/* Scrollable Organization List with dedicated scrollbar */}
              <div className="space-y-1 overflow-y-scroll flex-1 min-h-0 multi-subscribe-wizard__sidebar-scroll pr-1">
                {filteredOrgs.length === 0 ? (
                  <div className="py-8 text-center text-xs text-gray-400">
                    <p>No organizations found.</p>
                    {(searchTerm || providerFilter !== "all" || statusFilter !== "all") && (
                      <button
                        type="button"
                        onClick={() => {
                          setSearchTerm("");
                          setProviderFilter("all");
                          setStatusFilter("all");
                        }}
                        className="mt-2 font-semibold text-sky-600 hover:underline cursor-pointer"
                      >
                        Reset filters
                      </button>
                    )}
                  </div>
                ) : (
                  filteredOrgs.map((org) => {
                    const key = getOrgKey(org);
                    const plan = orgPlans[key] ?? orgPlans[org.id] ?? getDefaultOrgPlan();
                    const stepIdx = orgs.findIndex((o) => getOrgKey(o) === key);
                    const isActive = !showSummary && stepIdx === currentStepIndex;

                    return (
                      <OrgSidebarItem
                        key={key}
                        org={org}
                        plan={plan}
                        isActive={isActive}
                        onSelect={onOrgSelect}
                        onToggleInclude={handleToggleInclude}
                      />
                    );
                  })
                )}
              </div>

              {/* Sticky Sidebar Review & Confirm Button */}
              <div className="mt-2.5 border-t border-gray-100 pt-2 shrink-0">
                <button
                  type="button"
                  onClick={() => onOrgSelect?.("summary")}
                  className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-xs font-semibold transition cursor-pointer ${
                    showSummary
                      ? "bg-sky-600 text-white shadow-xs ring-2 ring-sky-400"
                      : "bg-sky-50 text-sky-700 hover:bg-sky-100"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <CheckCircle className={`h-4 w-4 ${showSummary ? "text-white" : "text-sky-600"}`} />
                    <span>Review & Confirm</span>
                  </span>
                  <span className={`rounded px-1.5 py-0.5 text-[11px] font-bold ${showSummary ? "bg-sky-700 text-white" : "bg-sky-200/80 text-sky-900"}`}>
                    {includedCount} Org{includedCount === 1 ? "" : "s"}
                  </span>
                </button>
              </div>
            </aside>

            {/* Right Content Panel with ALWAYS-VISIBLE SCROLLBAR SLIDER */}
            <div className="min-w-0 flex-1 h-full min-h-0 overflow-y-scroll multi-subscribe-wizard__content-scroll pr-3 pb-8">
              {showSummary ? (
                <SummaryStep
                  orgs={orgs}
                  orgPlans={orgPlans}
                  paymentCards={paymentCards}
                  defaultPaymentMethodId={defaultPaymentMethodId}
                  selectedPaymentMethodId={selectedPaymentMethodId}
                  onPaymentMethodChange={onPaymentMethodChange}
                  onAddNewCard={onAddNewCard}
                  onApplyCouponToAll={onApplyCouponToAll}
                  onRemoveCouponFromAll={onRemoveCouponFromAll}
                  onExcludeOrg={(orgKey) => handleToggleInclude(orgKey, false)}
                  onReincludeOrg={(orgKey) => handleToggleInclude(orgKey, true)}
                />
              ) : currentOrg ? (
                <div>
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 pb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <ProviderBadge provider={provider} />
                      <h3 className="text-base sm:text-lg font-bold text-gray-900 truncate">
                        {currentOrg.account}
                      </h3>
                    </div>

                    {/* Batch Apply Settings action */}
                    {onApplySettingsToAll && (
                      <button
                        type="button"
                        onClick={() =>
                          onApplySettingsToAll(currentPlan, provider)
                        }
                        title={`Apply this module & retention configuration to all ${provider === "qbo" ? "QuickBooks" : "Xero"} organizations`}
                        className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-semibold text-gray-700 shadow-2xs hover:bg-gray-50 transition cursor-pointer"
                      >
                        <Copy className="h-3.5 w-3.5 text-sky-600" />
                        <span>Apply to all {provider === "qbo" ? "QBO" : "Xero"} orgs</span>
                      </button>
                    )}
                  </div>

                  {/* Skip/Include Toggle Bar for Current Org */}
                  <div className="mb-3 flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="currentOrgIncludeToggle"
                        checked={currentPlan.accounts && !currentPlan.skipped}
                        onChange={(e) =>
                          handleToggleInclude(currentOrgKey, e.target.checked)
                        }
                        className="h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500 cursor-pointer"
                      />
                      <label
                        htmlFor="currentOrgIncludeToggle"
                        className="font-medium text-gray-800 cursor-pointer"
                      >
                        {currentPlan.accounts && !currentPlan.skipped
                          ? "Include in subscription batch"
                          : "Skip this organization"}
                      </label>
                    </div>

                    <span className="text-xs font-bold text-gray-700">
                      {currentPlan.accounts && !currentPlan.skipped
                        ? `$${computeSubscribeMonthlyTotal(currentPlan).toFixed(2)}/mo`
                        : "Skipped ($0.00)"}
                    </span>
                  </div>

                  <SubscribeModulePicker
                    provider={provider}
                    orgCountryCode={currentOrg.country_code}
                    subPlanFormData={currentPlan}
                    onSubPlanChange={handleSubPlanChange}
                    accountsRequired={false}
                    showPricingSummary={currentPlan.accounts && !currentPlan.skipped}
                    chargeImmediately={!!currentOrg.subscribe_will_charge_immediately}
                    introText={
                      currentPlan.skipped || !currentPlan.accounts
                        ? "This organization will be skipped unless you select Account."
                        : null
                    }
                  />

                  {currentPlan.accounts && !currentPlan.skipped && (
                    <div className="mt-4 border-t border-gray-100 pt-3">
                      <CouponInput
                        label="Have a Promo Code? (This Organization)"
                        helperText="Tip: If you want to apply one promo code across all organizations at once, you can do so on the Review step."
                        value={currentPlan.coupon_code || ""}
                        onChange={(code) =>
                          onPlanChange(currentOrgKey, { coupon_code: code })
                        }
                        appliedDiscount={currentPlan.coupon_discount}
                        onDiscountApplied={(discountInfo) =>
                          onPlanChange(currentOrgKey, {
                            coupon_discount: discountInfo,
                          })
                        }
                      />
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        ) : (
          /* Single Organization Layout - Clean & Compact without sidebar */
          <div className="border-t border-gray-100 pt-3.5 overflow-y-scroll multi-subscribe-wizard__content-scroll flex-1 min-h-0 pr-2">
            {showSummary ? (
              <SummaryStep
                orgs={orgs}
                orgPlans={orgPlans}
                paymentCards={paymentCards}
                defaultPaymentMethodId={defaultPaymentMethodId}
                selectedPaymentMethodId={selectedPaymentMethodId}
                onPaymentMethodChange={onPaymentMethodChange}
                onAddNewCard={onAddNewCard}
                onApplyCouponToAll={onApplyCouponToAll}
                onRemoveCouponFromAll={onRemoveCouponFromAll}
              />
            ) : currentOrg ? (
              <>
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <ProviderBadge provider={provider} />
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                    {currentOrg.account}
                  </h3>
                </div>

                <SubscribeModulePicker
                  provider={provider}
                  orgCountryCode={currentOrg.country_code}
                  subPlanFormData={currentPlan}
                  onSubPlanChange={handleSubPlanChange}
                  accountsRequired={false}
                  showPricingSummary={currentPlan.accounts && !currentPlan.skipped}
                  chargeImmediately={!!currentOrg.subscribe_will_charge_immediately}
                  introText={
                    currentPlan.skipped || !currentPlan.accounts
                      ? "This organization will be skipped unless you select Account."
                      : null
                  }
                />

                {currentPlan.accounts && !currentPlan.skipped && (
                  <div className="mt-4 border-t border-gray-100 pt-3.5">
                    <CouponInput
                      value={currentPlan.coupon_code || ""}
                      onChange={(code) =>
                        onPlanChange(currentOrgKey, { coupon_code: code })
                      }
                      appliedDiscount={currentPlan.coupon_discount}
                      onDiscountApplied={(discountInfo) =>
                        onPlanChange(currentOrgKey, {
                          coupon_discount: discountInfo,
                        })
                      }
                    />
                  </div>
                )}
              </>
            ) : null}
          </div>
        )}

        {/* Modal Bottom Footer Actions - Streamlined and Non-Duplicative */}
        <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3 shrink-0">
          <button
            type="button"
            onClick={showSummary ? onBack : onClose}
            className="rounded-lg border border-gray-300 px-4 sm:px-5 py-2 text-xs sm:text-sm text-gray-700 transition-colors hover:bg-gray-50 cursor-pointer"
          >
            {showSummary ? "Back to Edit" : "Cancel"}
          </button>

          <div className="flex items-center gap-2.5 sm:gap-3">
            {/* Previous button */}
            {!showSummary && isMulti && currentStepIndex > 0 && (
              <button
                type="button"
                onClick={() =>
                  onOrgSelect?.(getOrgKey(orgs[currentStepIndex - 1]))
                }
                className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-xs sm:text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
              >
                <ChevronLeft className="h-4 w-4" />
                <span>Previous</span>
              </button>
            )}

            {/* Next button (when more orgs remain) */}
            {!showSummary && isMulti && !isLastOrg && (
              <button
                type="button"
                onClick={onNext}
                className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 transition cursor-pointer"
              >
                <span>Next</span>
                <ChevronRight className="h-4 w-4 text-gray-500" />
              </button>
            )}

            {showSummary ? (
              <button
                type="button"
                onClick={() => onConfirm?.(currentPaymentMethodId)}
                disabled={confirming || includedCount === 0}
                className="rounded-lg px-5 sm:px-6 py-2 text-xs sm:text-sm font-semibold text-white transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer shadow-xs"
                style={{ backgroundColor: theme.primary }}
              >
                {confirming
                  ? "Processing…"
                  : paymentCards.length === 0
                  ? isMulti
                    ? `Add Card & Subscribe (${includedCount})`
                    : "Add Card & Subscribe"
                  : isMulti
                  ? `Confirm & Subscribe (${includedCount})`
                  : "Confirm & Subscribe"}
              </button>
            ) : (
              /* Single primary action to jump to Review & Confirm */
              <button
                type="button"
                onClick={() => onOrgSelect?.("summary")}
                className="inline-flex items-center gap-1.5 rounded-lg px-5 sm:px-6 py-2 text-xs sm:text-sm font-semibold text-white transition-colors hover:opacity-90 cursor-pointer shadow-xs"
                style={{ backgroundColor: theme.primary }}
              >
                <span>Review & Confirm ({includedCount})</span>
                <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
